import { inject, Injectable } from '@angular/core';
import { ISCDUser, ISCDUserDto, scdUserConverter } from '../interfaces/user/ISCDUser';
import { catchError, firstValueFrom, from, map, Observable, of, switchMap } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/default/environment';

@Injectable({
    providedIn: 'root'
})
export class SCDService {
    private http = inject(HttpClient);
    private msalService = inject(MsalService);

    private getAccessToken(): Observable<string> {
        const request = {
            scopes: environment.apiConfig[2].scopes,
            account: this.msalService.instance.getActiveAccount()
        };

        return from(this.msalService.instance.acquireTokenSilent(request)).pipe(
            map(result => result.accessToken),
            catchError(error => {
                console.log('Silent token acquisition failed, trying popup...', error);
                return from(this.msalService.instance.acquireTokenPopup(request)).pipe(
                    map(result => result.accessToken),
                    catchError(popupError => {
                        console.error('Popup token acquisition failed', popupError);
                        return of(null);
                    })
                );
            })
        );
    }

    public searchUserInSCD(
        firstName: string,
        surName: string,
        eMail: string,
        gid?: string
    ): Promise<{ records: ISCDUserDto[] }> {
        return this.getAccessToken()
            .pipe(
                switchMap(token => {
                    const headers = new HttpHeaders({
                        Authorization: 'Bearer ' + token,
                        'x-api-key': environment.scdApiKey,
                        'Content-Type': 'application/json'
                    });
                    return this.http.get(
                        environment.apiConfig[2].uri + this.buildUrlParameters(firstName, surName, eMail, gid),
                        { headers }
                    ) as Observable<{
                        records: ISCDUserDto[];
                    }>;
                })
            )
            .toPromise();
    }

    public async getUserFromSCDByGid(gid: string): Promise<ISCDUser> {
        const response = await firstValueFrom(
            this.getAccessToken().pipe(
                switchMap(token => {
                    const headers = new HttpHeaders({
                        Authorization: 'Bearer ' + token,
                        'x-api-key': environment.scdApiKey,
                        'Content-Type': 'application/json'
                    });
                    return this.http.get<{ records: ISCDUserDto[] }>(`${environment.apiConfig[2].uri}?gid=${gid}`, {
                        headers
                    });
                }),
                map(res => ({
                    records: scdUserConverter.toApiMulti(res.records)
                }))
            )
        );

        return response.records[0];
    }

    private buildUrlParameters(firstName: string, surName: string, eMail: string, gid?: string): string {
        const params = [
            ['firstName', firstName],
            ['surName', surName],
            ['mail', eMail],
            ['gid', gid]
        ].flatMap(([key, value]): string[] | string => (value ? `${key}=${value.trim().toLowerCase()}` : []));
        return params.length ? `?${params.join('&')}` : '';
    }
}
